import { DefaultTheme } from 'styled-components';

export const lightTheme: DefaultTheme = {
	body: '#FF0000',
	white: '#FFFFFF',
	gray: {
		0: '#FCFCFC',
		100: '#F6F6F6',
		200: '#E6E6E6',
		400: '#B2B2B2',
		600: '#606060',
		700: '#707070',
		800: '#909090',
		900: '#2f2f2f',
		1000: '#1A1A1A',
	},
	blue: {
		300: '#E5F4FF',
		400: '#1E90FF',
		500: '#008EF8',
		600: '#007DD4',
		700: '#0063ab',
		800: '#4169E1',
		900: '#8CDFF2',
		1000: '#C0F3FF',
	},
	red: {
		200: '#FECACA',
		500: '#EF4444',
		700: '#B23232',
	},
	orange: {
		50: '#FFF7ED',
		100: '#FFEDD5',
		200: '#FED7AA',
		300: '#FDBA74',
		700: '#C2410C',
	},
	green: {
		200: '#B4FEA0',
		400: '#97d587',
		600: '#5CAA3A',
		800: '#0DB43C',
	},
	purple: {
		300: '#EDE9FE',
		500: '#8B5CF6',
		700: '#7C3AED',
	},
};

export const darkTheme: DefaultTheme = {
	body: '#FF0000',
	white: '#1E1E1E',
	gray: {
		0: '#272727',
		100: '#313131',
		200: '#3C3C3C',
		400: '#7D7D7D',
		600: '#BDBDBD',
		700: '#C0C0C0',
		800: '#D8D8D8',
		900: '#F0F0F0',
		1000: '#FFFFFF',
	},
	blue: {
		300: '#4A5878',
		400: '#1E90FF',
		500: '#0080E0',
		600: '#007DD4',
		700: '#0586e3',
		800: '#4169E1',
		900: '#8CDFF2',
		1000: '#C0F3FF',
	},
	red: {
		200: '#7F1D1D',
		500: '#EF4444',
		700: '#F97878',
	},
	orange: {
		50: '#3A2E1F',
		100: '#432E1F',
		200: '#8B5A2B',
		300: '#C2794C',
		700: '#FB923C',
	},
	green: {
		200: '#4D8D32',
		400: '#5CAA3A',
		600: '#97d587',
		800: '#B4FEA0',
	},
	purple: {
		300: '#3B2E5A',
		500: '#8B5CF6',
		700: '#7C3AED',
	},
};

export const unthemed = lightTheme;

// Real-world post-it note colors -- intentionally outside the design system theme.
// These don't change with light/dark mode since they represent physical objects on the canvas.
export const stickyYellow = {
	fill: '#FFF9C4',
	fillHover: '#FFF59D',
	fillActive: '#FFF176',
	border: '#E6D154',
	shadow: 'rgba(0, 0, 0, 0.15)',
	fold: '#F0E68C',
	foldShadow: 'rgba(0, 0, 0, 0.08)',
	text: '#5D4E37',
};

//----------------------------------------------------------------------
// UI COLORS
//----------------------------------------------------------------------

// Canonical color themes for UI elements (buttons, chips, etc)
export type UiColor = 'blue' | 'purple' | 'green' | 'orange' | 'red' | 'gray' | 'black';

export interface ChipStyle {
	background: string;
	color: string;
}

export interface ButtonStyle {
	background: string;
	boxShadow: string;
	hoverBoxShadow: string;
	activeBoxShadow: string;
	splitMainBackground: string;
	splitDropdownBg: string;
	splitDropdownHoverBg: string;
	splitDropdownActiveBg: string;
}

// Hex to rgba helper for shadow colors
const hexToRgba = (hex: string, alpha: number): string => {
	const r = parseInt(hex.slice(1, 3), 16);
	const g = parseInt(hex.slice(3, 5), 16);
	const b = parseInt(hex.slice(5, 7), 16);
	return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

// Chip styles: soft tinted backgrounds with solid text
export const getChipStyle = (theme: DefaultTheme, color: UiColor): ChipStyle => {
	const map: Record<UiColor, ChipStyle> = {
		blue: { background: `${theme.blue[400]}33`, color: theme.blue[400] },
		purple: { background: `${theme.purple[500]}33`, color: theme.purple[500] },
		green: { background: `${theme.green[600]}33`, color: theme.green[600] },
		orange: { background: `${theme.orange[700]}33`, color: theme.orange[700] },
		red: { background: `${theme.red[500]}33`, color: theme.red[500] },
		gray: { background: theme.gray[200], color: theme.gray[800] },
		black: { background: theme.gray[800], color: theme.gray[100] },
	};
	return map[color];
};

// Button styles: gradients, shadows, split-button variants
export const getButtonStyle = (theme: DefaultTheme, color: UiColor): ButtonStyle => {
	const map: Record<UiColor, ButtonStyle> = {
		blue: {
			background: `linear-gradient(135deg, ${theme.blue[400]}, ${theme.blue[600]})`,
			boxShadow: `0 4px 12px ${hexToRgba(theme.blue[400], 0.25)}, 0 2px 4px rgba(0, 0, 0, 0.3)`,
			hoverBoxShadow: `0 6px 16px ${hexToRgba(
				theme.blue[400],
				0.35
			)}, 0 4px 8px rgba(0, 0, 0, 0.4)`,
			activeBoxShadow: `0 3px 10px ${hexToRgba(
				theme.blue[400],
				0.3
			)}, 0 2px 4px rgba(0, 0, 0, 0.3)`,
			splitMainBackground: `linear-gradient(135deg, ${theme.blue[400]}, ${theme.blue[800]})`,
			splitDropdownBg: theme.blue[500],
			splitDropdownHoverBg: theme.blue[400],
			splitDropdownActiveBg: theme.blue[700],
		},
		purple: {
			background: `linear-gradient(135deg, ${theme.purple[500]}, ${theme.purple[700]})`,
			boxShadow: `0 4px 12px ${hexToRgba(theme.purple[500], 0.25)}, 0 2px 4px rgba(0, 0, 0, 0.3)`,
			hoverBoxShadow: `0 6px 16px ${hexToRgba(
				theme.purple[500],
				0.35
			)}, 0 4px 8px rgba(0, 0, 0, 0.4)`,
			activeBoxShadow: `0 3px 10px ${hexToRgba(
				theme.purple[500],
				0.3
			)}, 0 2px 4px rgba(0, 0, 0, 0.3)`,
			splitMainBackground: `linear-gradient(135deg, ${theme.purple[500]}, ${theme.purple[700]})`,
			splitDropdownBg: theme.purple[700],
			splitDropdownHoverBg: theme.purple[500],
			splitDropdownActiveBg: theme.purple[700],
		},
		green: {
			background: `linear-gradient(135deg, ${theme.green[600]}, ${theme.green[800]})`,
			boxShadow: `0 4px 12px ${hexToRgba(theme.green[600], 0.25)}, 0 2px 4px rgba(0, 0, 0, 0.3)`,
			hoverBoxShadow: `0 6px 16px ${hexToRgba(
				theme.green[600],
				0.35
			)}, 0 4px 8px rgba(0, 0, 0, 0.4)`,
			activeBoxShadow: `0 3px 10px ${hexToRgba(
				theme.green[600],
				0.3
			)}, 0 2px 4px rgba(0, 0, 0, 0.3)`,
			splitMainBackground: `linear-gradient(135deg, ${theme.green[600]}, ${theme.green[800]})`,
			splitDropdownBg: theme.green[800],
			splitDropdownHoverBg: theme.green[600],
			splitDropdownActiveBg: theme.green[800],
		},
		orange: {
			background: `linear-gradient(135deg, ${theme.orange[300]}, ${theme.orange[700]})`,
			boxShadow: `0 4px 12px ${hexToRgba(theme.orange[700], 0.25)}, 0 2px 4px rgba(0, 0, 0, 0.3)`,
			hoverBoxShadow: `0 6px 16px ${hexToRgba(
				theme.orange[700],
				0.35
			)}, 0 4px 8px rgba(0, 0, 0, 0.4)`,
			activeBoxShadow: `0 3px 10px ${hexToRgba(
				theme.orange[700],
				0.3
			)}, 0 2px 4px rgba(0, 0, 0, 0.3)`,
			splitMainBackground: `linear-gradient(135deg, ${theme.orange[300]}, ${theme.orange[700]})`,
			splitDropdownBg: theme.orange[700],
			splitDropdownHoverBg: theme.orange[300],
			splitDropdownActiveBg: theme.orange[700],
		},
		red: {
			background: `linear-gradient(135deg, ${theme.red[500]}, ${theme.red[700]})`,
			boxShadow: `0 4px 12px ${hexToRgba(theme.red[500], 0.25)}, 0 2px 4px rgba(0, 0, 0, 0.3)`,
			hoverBoxShadow: `0 6px 16px ${hexToRgba(theme.red[500], 0.35)}, 0 4px 8px rgba(0, 0, 0, 0.4)`,
			activeBoxShadow: `0 3px 10px ${hexToRgba(theme.red[500], 0.3)}, 0 2px 4px rgba(0, 0, 0, 0.3)`,
			splitMainBackground: `linear-gradient(135deg, ${theme.red[500]}, ${theme.red[700]})`,
			splitDropdownBg: theme.red[700],
			splitDropdownHoverBg: theme.red[500],
			splitDropdownActiveBg: theme.red[700],
		},
		gray: {
			background: `linear-gradient(135deg, ${theme.gray[400]}, ${theme.gray[600]})`,
			boxShadow: `0 4px 12px rgba(0, 0, 0, 0.15), 0 2px 4px rgba(0, 0, 0, 0.3)`,
			hoverBoxShadow: `0 6px 16px rgba(0, 0, 0, 0.2), 0 4px 8px rgba(0, 0, 0, 0.4)`,
			activeBoxShadow: `0 3px 10px rgba(0, 0, 0, 0.15), 0 2px 4px rgba(0, 0, 0, 0.3)`,
			splitMainBackground: `linear-gradient(135deg, ${theme.gray[400]}, ${theme.gray[600]})`,
			splitDropdownBg: theme.gray[600],
			splitDropdownHoverBg: theme.gray[400],
			splitDropdownActiveBg: theme.gray[700],
		},
		black: {
			background: `linear-gradient(135deg, ${theme.gray[200]}, ${theme.white})`,
			boxShadow: `0 4px 12px rgba(0, 0, 0, 0.3), 0 2px 4px rgba(0, 0, 0, 0.4)`,
			hoverBoxShadow: `0 6px 16px rgba(0, 0, 0, 0.4), 0 4px 8px rgba(0, 0, 0, 0.5)`,
			activeBoxShadow: `0 3px 10px rgba(0, 0, 0, 0.3), 0 2px 4px rgba(0, 0, 0, 0.4)`,
			splitMainBackground: `linear-gradient(135deg, ${theme.gray[200]}, ${theme.white})`,
			splitDropdownBg: theme.white,
			splitDropdownHoverBg: theme.gray[200],
			splitDropdownActiveBg: theme.gray[400],
		},
	};
	return map[color];
};

export const shadows = {
	sm: '0 1px 4px rgba(0, 0, 0, 0.15)',
	md: '0 6px 12px rgba(0, 0, 0, 0.15)',
};

// the basic step size of our UI grid
export const step = 8;
//  sometimes, we use a half-step
export const halfStep = 4;

export const layout = {
	// the height (in px) of the header that appears above our frames, this never scales
	// even if the scene is scaling underneath it
	frameHeaderHeight: 18,
	panelInitialWidth: 270,
	chatPanelInitialWidth: 420,
	panelMinWidth: 100,
	panelMaxWidth: 600,
	inspectorNavbarHeight: 50,
	sidebarToggleHeight: 44,
	sidebarToggleWidth: 44,
};

export const zIndex = {
	//--------------------------------------------------
	// EDITING EXPERIENCE

	// the UI layer which hosts the the frame editing experience
	ui: 0,

	// navigator, property panels, etc
	panel: 3,
	panelOverlay: 4,

	modal: 5, // modals that float on top of our UI

	island: 7,

	loading: 9, // loading spinner

	sidebarOverlay: 999, // currently used for the opaque layer that lives underneath sidebar on mobile

	sidebar: 1000,

	sidebarToggle: 1001,

	contextMenu: 1002,

	blockingModal: 1100, // full-screen modals that block all interaction (showcase, pricing, etc.)
};

// Default preview background color for design system component/gallery previews
export const DS_EDITOR_PREVIEW_BG_DEFAULT = '#f8f8f8';
